import {useCallback, type Dispatch, type SetStateAction} from 'react'
import {SIN_RESPUESTA} from '@/lib/bateria'
import {alternarCasilla, senalando, type GestoEnCurso} from '@/components/bateria/cola/gesto-en-curso'
import {decisionBase, type DecisionCola, type DecisionLocal, type TarjetaCola} from '@/lib/bateria-cola'
import type {DestinoHoja} from '@/components/bateria/cola/tarjeta-ubicar'

export type GestosDeLaCola = {
  elegirMarca: (tarjeta: TarjetaCola, valor: string | null) => void
  cerrarGesto: (tarjeta: TarjetaCola) => void
  tachonSinRespuesta: (tarjeta: TarjetaCola) => void
  elegirCampo: (tarjeta: TarjetaCola, valor: string | null) => void
  senalarCasilla: (valor: string) => void
  /** Durante un gesto de SEÑALAR, el clic señala la casilla en vez de elegirla. */
  clicSenala: boolean
  asignarHoja: (tarjeta: TarjetaCola, elegido: DestinoHoja) => void
  descartarHoja: (tarjeta: TarjetaCola) => void
}

/** Traduce cada gesto de la cola a la única decisión que viaja al servidor. */
export function useGestosCola({
  gesto,
  setGesto,
  decisiones,
  setDecisiones,
  registrar,
  avanzar,
  olvidarDejada,
  descartarCuarentena,
}: {
  gesto: GestoEnCurso
  setGesto: Dispatch<SetStateAction<GestoEnCurso>>
  decisiones: Record<string, DecisionLocal>
  setDecisiones: Dispatch<SetStateAction<Record<string, DecisionLocal>>>
  registrar: (tarjeta: TarjetaCola, parcial: Partial<DecisionCola>) => void
  avanzar: () => void
  olvidarDejada: (ref: string) => void
  descartarCuarentena: (vars: {cuarentenaId: string; motivo: string | null}) => void
}): GestosDeLaCola {
  const elegirMarca = useCallback(
    (tarjeta: TarjetaCola, valor: string | null) => {
      if (valor === null) {
        // `null` es volver a elegir la opción marcada: en una tarjeta ya decidida la confirma y avanza.
        if (tarjeta.ref in decisiones) avanzar()
        return
      }
      if (valor === SIN_RESPUESTA) {
        // Dentro de un gesto, «en blanco» es «tachué todo y no quedó ninguna válida», no un blanco.
        registrar(
          tarjeta,
          gesto ? {valor: null, gesto: gesto.tipo, casillas: gesto.casillas} : {valor: null, gesto: 'blanco'}
        )
        return
      }
      // La respuesta válida de un tachón viaja con su gesto y sus casillas: el motor las necesita.
      registrar(tarjeta, gesto ? {valor, gesto: gesto.tipo, casillas: gesto.casillas} : {valor})
    },
    [registrar, decisiones, avanzar, gesto]
  )

  const cerrarGesto = useCallback(
    (tarjeta: TarjetaCola) => {
      if (!gesto) return
      registrar(tarjeta, {valor: null, gesto: gesto.tipo, casillas: gesto.casillas})
    },
    [gesto, registrar]
  )

  // Misma decisión que cerrar el gesto con Enter, sin tener que abrirlo con `T`.
  const tachonSinRespuesta = useCallback(
    (tarjeta: TarjetaCola) => registrar(tarjeta, {valor: null, gesto: 'tachon', casillas: gesto?.casillas ?? []}),
    [gesto, registrar]
  )

  const elegirCampo = useCallback(
    (tarjeta: TarjetaCola, valor: string | null) => {
      if (valor === null) registrar(tarjeta, {valor: null, gesto: 'blanco'})
      else if (gesto) registrar(tarjeta, {valor, gesto: gesto.tipo, casillas: gesto.casillas})
      else registrar(tarjeta, {valor})
    },
    [gesto, registrar]
  )

  /** Dentro del gesto en curso, dice qué casillas del papel tienen marca; no decide. */
  const senalarCasilla = useCallback(
    (valor: string) => {
      setGesto((previo) => (previo ? alternarCasilla(previo, valor) : previo))
    },
    [setGesto]
  )

  // Misma regla que el teclado, para que el número y el clic nunca difieran sobre una casilla.
  const clicSenala = senalando(gesto)

  const asignarHoja = useCallback(
    (tarjeta: TarjetaCola, elegido: DestinoHoja) => {
      if (elegido.ref !== tarjeta.ref) return
      registrar(tarjeta, {paquete_id: elegido.paqueteId, numero: elegido.numero})
    },
    [registrar]
  )

  const descartarHoja = useCallback(
    (tarjeta: TarjetaCola) => {
      const cuarentenaId = tarjeta.ref.split(':')[1]
      descartarCuarentena({cuarentenaId, motivo: null})
      setDecisiones((previas) => ({
        ...previas,
        [tarjeta.ref]: {decision: decisionBase(tarjeta), estado: 'enviada'},
      }))
      olvidarDejada(tarjeta.ref)
      avanzar()
    },
    [descartarCuarentena, setDecisiones, avanzar, olvidarDejada]
  )

  return {
    elegirMarca,
    cerrarGesto,
    tachonSinRespuesta,
    elegirCampo,
    senalarCasilla,
    clicSenala,
    asignarHoja,
    descartarHoja,
  }
}
